import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ComponentProps, MouseEvent } from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input, InputProps } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Type definitions for InputGroup components
type InputGroupAlign = "inline-start" | "inline-end" | "block-start" | "block-end";
type InputGroupButtonSize = "xs" | "sm" | "icon-xs" | "icon-sm";
type InputGroupButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";

type InputGroupAddonProps = HTMLAttributes<HTMLDivElement> & {
    className?: string;
    align?: InputGroupAlign;
    onClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

type InputGroupButtonProps = Omit<ComponentProps<typeof Button>, "size"> & {
    className?: string;
    type?: "button" | "submit" | "reset";
    variant?: InputGroupButtonVariant;
    size?: InputGroupButtonSize;
};

const InputGroup = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            data-slot="input-group"
            role="group"
            className={cn(
                "setu:group/input-group setu:!border-input setu:dark:bg-input/30 setu:shadow-xs setu:relative setu:flex setu:w-full setu:items-center setu:rounded-md setu:!border setu:outline-none setu:transition-[color,box-shadow]",
                "setu:h-9 setu:has-[>textarea]:h-auto",

                // Variants based on alignment.
                "setu:has-[>[data-align=inline-start]]:[&>input]:pl-2",
                "setu:has-[>[data-align=inline-end]]:[&>input]:pr-2",
                "setu:has-[>[data-align=block-start]]:h-auto setu:has-[>[data-align=block-start]]:flex-col setu:has-[>[data-align=block-start]]:[&>input]:pb-3",
                "setu:has-[>[data-align=block-end]]:h-auto setu:has-[>[data-align=block-end]]:flex-col setu:has-[>[data-align=block-end]]:[&>input]:pt-3",

                // Focus state.
                "setu:has-[[data-slot=input-group-control]:focus-visible]:!border-ring setu:has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50 setu:has-[[data-slot=input-group-control]:focus-visible]:ring-[3px]",

                // Error state.
                "setu:has-[[data-slot][aria-invalid=true]]:ring-destructive/20 setu:has-[[data-slot][aria-invalid=true]]:border-destructive setu:dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40",

                className
            )}
            {...props}
        />
    );
};

const inputGroupAddonVariants = cva(
    "setu:text-muted-foreground setu:flex setu:h-auto setu:cursor-text setu:select-none setu:items-center setu:justify-center setu:gap-2 setu:py-1.5 setu:text-sm setu:font-medium setu:group-data-[disabled=true]/input-group:opacity-50 setu:[&>kbd]:rounded-[calc(var(--radius)-5px)] setu:[&>svg:not([class*='size-'])]:size-4",
    {
        variants: {
            align: {
                "inline-start":
                    "setu:order-first setu:pl-3 setu:has-[>button]:ml-[-0.45rem] setu:has-[>kbd]:ml-[-0.35rem]",
                "inline-end": "setu:order-last setu:pr-3 setu:has-[>button]:mr-[-0.4rem] setu:has-[>kbd]:mr-[-0.35rem]",
                "block-start":
                    "setu:[.border-b]:pb-3 setu:order-first setu:w-full setu:justify-start setu:px-3 setu:pt-3 setu:group-has-[>input]/input-group:pt-2.5",
                "block-end":
                    "setu:[.border-t]:pt-3 setu:order-last setu:w-full setu:justify-start setu:px-3 setu:pb-3 setu:group-has-[>input]/input-group:pb-2.5",
            },
        },
        defaultVariants: {
            align: "inline-start",
        },
    }
);

const InputGroupAddon = ({
    className,
    align = "inline-start",
    ...props
}: InputGroupAddonProps & VariantProps<typeof inputGroupAddonVariants>) => {
    return (
        <div
            role="group"
            data-slot="input-group-addon"
            data-align={align}
            className={cn(inputGroupAddonVariants({ align }), className)}
            onClick={(e) => {
                if ((e.target as HTMLElement).closest("button")) {
                    return;
                }
                e.currentTarget.parentElement?.querySelector("input")?.focus();
            }}
            {...props}
        />
    );
};

const inputGroupButtonVariants = cva("setu:flex setu:items-center setu:gap-2 setu:text-sm setu:shadow-none", {
    variants: {
        size: {
            xs: "setu:!h-6 setu:!gap-1 setu:!rounded-[calc(var(--radius)-5px)] setu:!px-2 setu:has-[>svg]:px-2 setu:[&>svg:not([class*='size-'])]:size-3.5",
            sm: "setu:!h-8 setu:!gap-1.5 setu:!rounded-md setu:!px-2.5 setu:has-[>svg]:px-2.5",
            "icon-xs": "setu:!size-6 setu:!rounded-[calc(var(--radius)-5px)] setu:!p-0 setu:has-[>svg]:p-0",
            "icon-sm": "setu:!size-8 setu:p-0 setu:has-[>svg]:p-0",
        },
    },
    defaultVariants: {
        size: "xs",
    },
});

const InputGroupButton = React.forwardRef<
    HTMLButtonElement,
    InputGroupButtonProps & VariantProps<typeof inputGroupButtonVariants>
>(({ className, type = "button", variant = "ghost", size = "xs", ...props }, ref) => {
    return (
        <Button
            type={type}
            data-size={size}
            variant={variant}
            className={cn(inputGroupButtonVariants({ size }), className)}
            {...props}
            ref={ref}
        />
    );
});
InputGroupButton.displayName = "InputGroupButton";

const InputGroupText = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => {
    return (
        <span
            className={cn(
                "setu:text-muted-foreground setu:flex setu:items-center setu:gap-2 setu:text-sm setu:[&_svg:not([class*='size-'])]:size-4 setu:[&_svg]:pointer-events-none",
                className
            )}
            {...props}
        />
    );
};

const InputGroupInput = React.forwardRef<HTMLInputElement, InputProps>(({ className, ...props }, ref) => {
    return (
        <Input
            data-slot="input-group-control"
            className={cn(
                "setu:flex-1 setu:rounded-none setu:!border-0 setu:bg-transparent setu:shadow-none setu:focus-visible:ring-0 setu:dark:bg-transparent",
                className
            )}
            {...props}
            ref={ref}
        />
    );
});
InputGroupInput.displayName = "InputGroupInput";

const InputGroupTextarea = React.forwardRef<HTMLTextAreaElement, ComponentProps<typeof Textarea>>(
    ({ className, ...props }, ref) => {
        return (
            <Textarea
                data-slot="input-group-control"
                className={cn(
                    "setu:flex-1 setu:resize-none setu:rounded-none setu:!border-0 setu:bg-transparent setu:py-3 setu:shadow-none setu:focus-visible:ring-0 setu:dark:bg-transparent",
                    className
                )}
                {...props}
                ref={ref}
            />
        );
    }
);
InputGroupTextarea.displayName = "InputGroupTextarea";

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupText, InputGroupInput, InputGroupTextarea };

// Export types for external use
export type {
    InputGroupAddonProps,
    InputGroupButtonProps,
    InputGroupAlign,
    InputGroupButtonSize,
    InputGroupButtonVariant,
};
